// src/lib/date-fr-ordinal.js — « le 1er août », jamais « le 1 août ». (ESM client.)
//
// ⚠️ DUPLICATION CONTRÔLÉE de lib/date-fr-ordinal.js (CommonJS serveur) — ADR-021.
// Toute modification de `frPremierDuMois` doit être appliquée AUX DEUX fichiers.
// Justification : `ModuleScopePlugin` (CRA) INTERDIT à `src/` d'importer `lib/`,
// et le serveur Node ne peut pas charger l'ESM du bundle. Le miroir est le seul
// pont possible ; la PARITÉ est verrouillée par
// src/lib/date-fr-ordinal-cjs-parity.test.js (matrice complète + surface
// d'export). Un helper ajouté d'un seul côté fait rougir la CI.
//
// ── LA RÈGLE ────────────────────────────────────────────────────────────────
//
// LE DÉFAUT VU PAR LE PROPRIÉTAIRE (2026-07-30) : le message de blocage du
// livret rendait « Le dépôt de garantie sera possible à partir du 1 août
// 2026 ». En français, le PREMIER jour du mois prend l'ordinal — « 1er » — et
// lui SEUL : le 2, le 3, le 21, le 31 restent cardinaux (jamais « 2ème août »).
// `Intl` ne le fait pas : aucune locale FR ne rend l'ordinal du quantième,
// c'est donc à nous.
//
// L'ANGLAIS ET L'ESPAGNOL SONT CORRECTS TELS QUELS et ne sont PAS touchés :
// « 1 August 2026 » et « 1 de agosto de 2026 » sont les formes attendues de
// leurs locales respectives. La règle est bornée au français, à la locale ET au
// quantième 1.
//
// ── POURQUOI CE MODULE EXISTE (2026-07-30, second passage) ──────────────────
//
// La règle a d'abord vécu en helper PRIVÉ dans src/JourneyPage.jsx, où elle ne
// servait que les ÉCRANS. Le propriétaire a élargi : « ça concerne tout ce que
// j'envoie, pas juste ce qui s'affiche ». Or les EMAILS et les SMS voyageur
// passent par un tout autre formateur — `fmtDate` de template-variables — qui
// rendait encore « 1 août 2026 ».
//
// Plutôt que d'ajouter un TROISIÈME mécanisme, la règle est EXTRAITE ici et les
// trois formateurs la consomment :
//   • src/JourneyPage.jsx   → formatDateFR / formatLocaleDate  (écrans)
//   • src/lib/template-variables.js → fmtDate  (aperçus admin, ESM)
//   • lib/template-variables.js     → fmtDate  (emails + SMS réels, CJS)
// Jamais de correctif au point d'appel — sinon la prochaine phrase datée
// réintroduirait « le 1 » en silence.

/**
 * Applique l'ordinal du premier jour du mois à une date DÉJÀ formatée.
 *
 * Volontairement post-formatage plutôt que reconstructif : on ne réimplémente
 * pas `Intl` (mois, ordre des tokens, année), on corrige le SEUL token que les
 * locales FR rendent mal.
 *
 * @param {string} txt  Sortie d'un `toLocaleDateString` / `Intl.DateTimeFormat`.
 * @param {Date}   date L'objet Date qui a produit `txt` (mêmes fuseau/locale).
 * @param {string} loc  La locale utilisée pour produire `txt` (ex. 'fr-FR').
 * @returns {string} `txt` inchangé, ou son quantième de tête passé en « 1er ».
 */
export function frPremierDuMois(txt, date, loc) {
  if (typeof txt !== "string" || !txt) return txt;
  if (typeof loc !== "string" || !loc.startsWith("fr")) return txt;
  // ⚠️ CE GARDE SUPPOSE QUE `txt` A ÉTÉ RENDU DANS LE FUSEAU AMBIANT.
  // `getDate()` est LOCAL, comme `Intl` SANS option `timeZone` : les deux
  // lectures parlent alors du même jour, et l'ordinal ne peut pas se poser sur
  // le mauvais quantième. Un appelant qui passerait un `timeZone:` EXPLICITE
  // différent du fuseau du process casserait cette correspondance — la
  // signature ne permet pas de le lui dire. Aucun appelant actuel ne le fait ;
  // le prochain qui en a besoin doit ÉTENDRE ce helper, pas l'appeler quand
  // même. (Cas connu : src/PublicLivretModuleDetail.jsx, qui formate en
  // `timeZone: 'Europe/Paris'` — volontairement NON converti pour cette raison.)
  //
  // ⚠️ DETTE PRÉ-EXISTANTE, NON INTRODUITE ICI ET NON CORRIGÉE ICI : `fmtDate`
  // construit ses dates avec `new Date('2026-08-01')`, soit minuit UTC. En
  // fuseau NÉGATIF (Vercel est en UTC, mais un navigateur admin à New York ne
  // l'est pas), ce même ISO s'affiche « 31 juillet 2026 » — un JOUR DE MOINS.
  // Ce helper reste cohérent avec ce rendu (le garde vaut 31, aucun « er »
  // posé : on n'écrit jamais « 1er 31 juillet »), mais QUE LES CHOSES SOIENT
  // CLAIRES — « 31 juillet » pour un séjour qui commence le 1er août est une
  // DATE FAUSSE, pas un ordinal correctement décliné. Le défaut est dans
  // `fmtDate`, il est antérieur à ce module, et il mérite son propre lot.
  if (!(date instanceof Date) || date.getDate() !== 1) return txt;
  // Ancré en DÉBUT de chaîne et suivi ni d'un chiffre ni d'un « er » déjà posé.
  //   • `(?!\d)`  → « 11 » / « 12 » ne deviennent JAMAIS « 1er1 » / « 1er2 ».
  //     Le garde `getDate() === 1` les rend déjà inatteignables ; l'ancre et la
  //     négation sont la SECONDE barrière (le piège est testé aux deux niveaux).
  //   • `(?!er)`  → IDEMPOTENCE. Sans elle, « 1er août » redeviendrait
  //     « 1erer août » à une seconde application. Aucun appelant actuel ne
  //     double la passe (l'entrée est toujours une sortie fraîche d'`Intl`, qui
  //     n'écrit jamais « 1er »), mais ce module a désormais TROIS consommateurs
  //     et un helper partagé doit survivre à un appel de trop. Durcissement
  //     assumé par rapport à la version d'origine, à comportement identique sur
  //     toute entrée réelle.
  //
  // ⚠️ PORTÉE DE L'ANCRE `^` : seul un quantième EN TÊTE de chaîne est touché.
  // Un format préfixé du JOUR DE LA SEMAINE (« samedi 1 août 2026 ») ne reçoit
  // donc AUCUN ordinal. Aucun appelant n'utilise `weekday` aujourd'hui — c'est
  // une limite connue, pas un oubli. L'ancre protège en échange TOUS les
  // formats numériques (`01/08/2026`, `2026-08-01` de fr-CA, `01.08.2026` de
  // fr-CH), qui commencent par `0` ou `2` et ne matchent jamais.
  return txt.replace(/^1(?!\d|er)/, "1er");
}
